import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useLocation } from 'react-router-dom';
import { config } from "node-config-ts";
import queryString from "query-string";
import { LocaleTypes } from "@iddataweb/idw-common-ui/enums/locale-types";
import { setQueryParameters } from "@redux/actions/query-parameters-actions";
import { Logger } from "@utils/logger";

export const useQueryParameters = () => {
    const dispatch = useDispatch();
    const location = useLocation();

    const getQueryParameters = () => {
        const query = location.search || window.location.search;
        let queryParameters = queryString.parse(query, { decode: true });
        const pathName = location.pathname || window.location.pathname;
        Logger.info(`[App] Launching the AXNUI App from query: ${query}`);
        Logger.info(`[App] Parsed Query params: ${JSON.stringify(queryParameters || {})}`, queryParameters);
        Logger.info('Context Path to use: ', queryParameters.contextpath);
        Logger.info('Current path: ', pathName);

        if (config.REACT_APP_USE_MOCK_AXN) {
            const axnContext = config.REACT_APP_USE_MOCK_AXN_BASE_CONTEXT;
            const contextPath = config.REACT_APP_MOCK_AXN_URL;

            queryParameters = {
                ...queryParameters,
                token: `${new Date().getTime()}`,
                asi: `${new Date().getTime()}`,
                contextpath: `${contextPath}${axnContext}`
            };
        }

        return queryParameters;
    };

    const getFinalConfigUrl = (configUrl: string, queryParameters: queryString.ParsedQuery<string>) => {
        let finalConfigUrl = configUrl;
        if (queryParameters.client_id && queryParameters.redirect_uri) {
            const encodedRedirectURI = encodeURIComponent(queryParameters.redirect_uri as string);
            finalConfigUrl += `&redirect_uri=${encodedRedirectURI}&client_id=${queryParameters.client_id}`;
        }
        return finalConfigUrl;
    };

    useEffect(() => {
        const queryParameters = getQueryParameters();
        let configUrl = "";
        if (queryParameters.contextpath) {
            configUrl = `${queryParameters.contextpath}`;
            if (!queryParameters.contextpath.includes("/axn")) {
                configUrl += "/axn";
            }
            configUrl += `/gateway/config`;

            // normal flow - hit authenticated config route
            if (queryParameters.asi && queryParameters.token) {
                configUrl = getFinalConfigUrl(`${configUrl}?asi=${queryParameters.asi}&token=${queryParameters.token}`, queryParameters);
            }
            // mobile-complete or mobile-wait flow - hit config/base
            else if (queryParameters.asi && queryParameters.flow) {
                configUrl = getFinalConfigUrl(`${configUrl}/base?asi=${queryParameters.asi}`, queryParameters);
            }
            // themed error flow - hit unauthenticated config route
            else if (queryParameters.asi) {
                configUrl = getFinalConfigUrl(`${configUrl}?asi=${queryParameters.asi}`, queryParameters);
            }
            // error flow - dont hit config route
            else if (queryParameters.referenceid) {
                configUrl = null;
            }
        } else if (queryParameters.flow && queryParameters.flow === "mobile_complete") {
            configUrl = null;
        }

        if (config.REACT_APP_USE_MOCK_AXN) {
            configUrl = queryParameters.contextpath as string;
        }

        dispatch(setQueryParameters({
            token: queryParameters.token as string,
            asi: queryParameters.asi as string,
            contextPath: queryParameters.contextpath as string,
            userMessage: queryParameters.msg as string,
            configUrl: configUrl,
            country: queryParameters.country ? queryParameters.country as string : null,
            errorCode: (queryParameters.errorcode || queryParameters.errorCode || queryParameters.error) as string,
            language: queryParameters.language as LocaleTypes,
            referenceId: queryParameters.referenceid as string,
            apSessionId: queryParameters.apSessionId as string,
            flow: queryParameters.flow as string,
            initialized: true
        }));
    }, []);
};